import React from 'react';
import './ThankYouPage.css';
// You'll need to add these images to your Firebase project's public/assets folder
// or import them from your assets directory
const ASSETS = {
  msdTriangle: '/assets/msd_triangle.png', // Download from: https://d11n7da8rpqbjy.cloudfront.net/liveanddare/145639554429MSD_Triangle.png
  googlePlayBadge: '/assets/google_play_badge.png',
  appStoreBadge: '/assets/app_store_badge.png'
};

// Custom Logo Component with Triangle and Montserrat Text
const HigherMindLogo = () => {
  return (
    <div className="flex items-center space-x-3" style={{ marginLeft: -50 }}>
      {/* Triangle Image */}
      <div className="w-8 h-8 md:w-10 md:h-10">
        <img
          src={ASSETS.msdTriangle}
          alt="Higher Mind Triangle"
          className="w-full h-full object-contain drop-shadow-sm"
        />
      </div>
      {/* Higher Mind Text */}
      <div
        className="text-white font-bold text-lg md:text-xl tracking-wide"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        HIGHER MIND
      </div>
    </div>
  );
};

// Check Circle Icon Component (inline SVG to avoid external dependencies)
const CheckCircleIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginBottom: -3 }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// Smartphone Icon Component (inline SVG to avoid external dependencies)
const SmartphoneIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1E3A8A"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />

    <rect x="6" y="3" width="12" height="18" rx="1.5" ry="1.5" fill="none" stroke="#1E3A8A" strokeWidth="1" strokeOpacity="0.5" />

    <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="#1E3A8A" strokeWidth="0.6666666666666666" />
  </svg>
);

// Main Button Component
const Button = ({ onClick, className, children, ...props }) => (
  <button
    onClick={onClick}
    style={{ border: "none", outline: "none" }}
    className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background ${className}`}
    {...props}
  >
    {children}
  </button>
);

const ThankYouPage = () => {
  const handleOpenApp = () => {
    window.location.href = 'highermind://login';
  };

  const handleGooglePlay = () => {
    window.open('https://play.google.com/store/apps/details?id=com.mindfulselfdiscipline&hl=en', '_blank');
  };

  const handleAppStore = () => {
    window.open('https://apps.apple.com/au/app/higher-mind/id1557827729', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex flex-col">
      {/* Header with Custom Logo */}
      <header className="w-full px-6 py-8 flex justify-center">
        <HigherMindLogo />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-16">
        <div className="max-w-md w-full text-center space-y-8">

          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="bg-green-500 rounded-full p-4 animate-pulse">
              <CheckCircleIcon className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Thank You Message */}
          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Thank You!
            </h1>
            <p className="text-blue-200 text-base leading-relaxed">
              Welcome to your journey of self-discipline, focus, and self-mastery with the Higher Mind System.
            </p>
          </div>

          {/* Open App Button */}
          <div className="space-y-4">
            <Button
              onClick={handleOpenApp}
              className="flex w-full bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <div className='mr-2 flex'>
                <SmartphoneIcon className="w-5 h-5" />
              </div>
              Open Higher Mind App
            </Button>
            <p className="text-sm text-blue-300">
              Click above if you have the app installed
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center space-x-4 my-8">
            <div className="flex-1 h-px bg-blue-600"></div>
            <span className="text-blue-300 text-sm px-6">or download the app</span>
            <div className="flex-1 h-px bg-blue-600"></div>
          </div>

          {/* App Store Buttons */}
          <div>
            <button
              onClick={handleGooglePlay}
              className="block w-full transition-transform duration-200 hover:scale-105 plain-button"
            >
              <img
                src={ASSETS.googlePlayBadge}
                alt="Get it on Google Play"
                className="h-auto mx-auto"
                style={{ width: 210 }}
              />
            </button>

            <button
              onClick={handleAppStore}
              className="block w-full transition-transform duration-200 hover:scale-105 plain-button"
            >
              <div className="flex justify-center">
                <div className="bg-black rounded-lg px-6 py-3 flex items-center space-x-3">
                  <div className="text-white">
                    <div className="text-xs">Download on the</div>
                    <div className="text-xl font-semibold -mt-1">App Store</div>
                  </div>
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      {/* <footer className="text-center py-6 px-6">
        <p className="text-blue-300 text-sm">
          Start your journey to self-mastery today
        </p>
      </footer> */}
    </div>
  );
};

export default ThankYouPage;
